/**
 * Several employees for one create (#2121): chips in the field, a searchable list below.
 * Type to search, Enter adds the highlighted person, Backspace in the empty search removes the
 * last chip. "Open shift" is exclusive: it replaces the chips, and a person replaces it.
 * Each chip can carry a marker (overlap, absence, availability) with an icon, a short word
 * and a tooltip naming the days.
 */
import * as React from 'react';
import { useLingui } from '@lingui/react';
import { AlertTriangle, CalendarX2, Check, ChevronDown, CircleDashed, Loader2, Search, UserX, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { isSelectableEmployee, useAllEmployees } from '@/hooks/useEmployees';
import { cn } from '@/lib/utils';
import { MAX_SHIFT_EMPLOYEES, OPEN_SHIFT_VALUE } from '@/schemas/shiftSchemas';
import { EmployeeAvatar } from './EmployeeSelect';

export type EmployeeMarkerKind = 'overlap' | 'absent' | 'availability';

/** What the inline check found for one person; `detail` is the tooltip (names the days). */
export interface EmployeeMarker {
  kind: EmployeeMarkerKind;
  detail: string;
}

export interface EmployeeChipSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  id?: string;
  /** Accessible name of the field (the visible label is not linked to the custom trigger). */
  'aria-label'?: string;
  'aria-describedby'?: string;
  error?: string;
  /** Soft filter to the planned groups, as in EmployeeSelect (#350). */
  groupIds?: string[];
  /** Offers "Open shift (nobody assigned)" first (#2040); exclusive with people. */
  allowOpenShift?: boolean;
  /** The inline check per person. */
  markers?: Record<string, EmployeeMarker | undefined>;
}

const MARKER_ICON = { overlap: AlertTriangle, absent: UserX, availability: CalendarX2 } as const;

export function EmployeeChipSelect({
  value,
  onChange,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  error,
  groupIds,
  allowOpenShift = false,
  markers,
}: EmployeeChipSelectProps) {
  const { i18n } = useLingui();
  const { data: employeeList, isLoading, isError } = useAllEmployees();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const [showAll, setShowAll] = React.useState(false);
  const listId = React.useId();
  const searchRef = React.useRef<HTMLInputElement>(null);

  const plannedGroupIds = React.useMemo(() => groupIds ?? [], [groupIds]);
  const filterActive = plannedGroupIds.length > 0 && !showAll;
  const isOpenShift = value[0] === OPEN_SHIFT_VALUE;

  const byId = React.useMemo(() => new Map((employeeList ?? []).filter((e) => e.id).map((e) => [e.id!, e])), [employeeList]);
  const nameOf = (employeeId: string) => {
    const employee = byId.get(employeeId);
    return employee ? `${employee.firstname ?? ''} ${employee.lastname ?? ''}`.trim() : employeeId;
  };

  const openShiftLabel = i18n._({ id: 'shifts.form.openShiftOption', message: 'Open shift (nobody assigned)' });
  const term = query.trim().toLowerCase();
  const options = React.useMemo(() => {
    const employees = (employeeList ?? []).filter(
      (employee) =>
        !!employee.id &&
        (value.includes(employee.id) || isSelectableEmployee(employee)) &&
        (!filterActive ||
          value.includes(employee.id) ||
          employee.allGroups === true ||
          (employee.groupIds?.some((g) => plannedGroupIds.includes(g)) ?? false))
    );
    const people = employees
      .map((employee) => ({ value: employee.id!, label: `${employee.firstname ?? ''} ${employee.lastname ?? ''}`.trim() }))
      .filter((option) => !term || option.label.toLowerCase().includes(term));
    const openOption =
      allowOpenShift && (!term || openShiftLabel.toLowerCase().includes(term)) ? [{ value: OPEN_SHIFT_VALUE, label: openShiftLabel }] : [];
    return [...openOption, ...people];
  }, [employeeList, value, filterActive, plannedGroupIds, term, allowOpenShift, openShiftLabel]);

  const toggle = (optionValue: string) => {
    if (optionValue === OPEN_SHIFT_VALUE) {
      onChange(isOpenShift ? [] : [OPEN_SHIFT_VALUE]);
      setOpen(false);
      return;
    }
    const people = isOpenShift ? [] : value;
    if (people.includes(optionValue)) {
      onChange(people.filter((v) => v !== optionValue));
    } else if (people.length < MAX_SHIFT_EMPLOYEES) {
      onChange([...people, optionValue]);
    }
    setQuery('');
  };

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((a) => Math.min(a + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = options[active] ?? options[0];
      if (option) toggle(option.value);
    } else if (event.key === 'Backspace' && query === '' && value.length > 0) {
      event.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  const markerWord = (kind: EmployeeMarkerKind) =>
    kind === 'overlap'
      ? i18n._({ id: 'shifts.employeeChip.overlap', message: 'Conflict' })
      : kind === 'absent'
        ? i18n._({ id: 'shifts.employeeChip.absent', message: 'Absent' })
        : i18n._({ id: 'shifts.employeeChip.availability', message: 'Availability' });

  if (isLoading) {
    return (
      <div className="flex h-10 items-center gap-2 rounded-md border bg-muted px-3">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm text-muted-foreground">{i18n._({ id: 'common.loading', message: 'Loading...' })}</span>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex h-10 items-center rounded-md border border-destructive bg-destructive/10 px-3">
        <span className="text-sm text-destructive">
          {i18n._({ id: 'shifts.form.employeeLoadError', message: 'Failed to load employees' })}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <TooltipProvider delayDuration={150}>
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setQuery('');
          }}
        >
          <PopoverAnchor asChild>
            <div
              className={cn(
                'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5',
                error && 'border-destructive'
              )}
              data-testid="employee-multiselect"
            >
              {value.map((chipId) => {
                const marker = markers?.[chipId];
                const MarkerIcon = marker ? MARKER_ICON[marker.kind] : null;
                const label = chipId === OPEN_SHIFT_VALUE ? openShiftLabel : nameOf(chipId);
                const employee = byId.get(chipId);
                return (
                  <span
                    key={chipId}
                    className={cn(
                      'inline-flex max-w-full items-center gap-1 rounded-full border bg-muted/60 py-0.5 pl-1 pr-0.5 text-sm',
                      marker && 'border-warning-on-surface/50 bg-warning-muted'
                    )}
                    data-testid={`employee-chip-${chipId}`}
                  >
                    {chipId === OPEN_SHIFT_VALUE ? (
                      <CircleDashed className="h-4 w-4 shrink-0" aria-hidden="true" />
                    ) : employee ? (
                      <EmployeeAvatar employee={{ id: employee.id, firstname: employee.firstname, lastname: employee.lastname, avatarUrl: employee.avatarUrl }} />
                    ) : null}
                    <span className="truncate">{label}</span>
                    {marker && MarkerIcon && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            tabIndex={0}
                            className="inline-flex items-center gap-0.5 rounded px-1 text-xs font-medium text-warning-on-surface"
                            data-testid={`employee-chip-marker-${chipId}`}
                          >
                            <MarkerIcon className="h-3 w-3" aria-hidden="true" />
                            {markerWord(marker.kind)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-left font-normal">{marker.detail}</TooltipContent>
                      </Tooltip>
                    )}
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={i18n._({ id: 'shifts.employeeChip.remove', message: 'Remove {name}', values: { name: label } })}
                      onClick={() => onChange(value.filter((v) => v !== chipId))}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                );
              })}
              <PopoverTrigger asChild>
                <button
                  type="button"
                  id={id}
                  role="combobox"
                  aria-expanded={open}
                  aria-haspopup="listbox"
                  aria-controls={listId}
                  aria-label={ariaLabel}
                  aria-describedby={ariaDescribedBy}
                  aria-invalid={!!error}
                  className="flex h-7 min-w-32 flex-1 items-center justify-between gap-2 rounded-sm px-1 text-left text-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="employee-multiselect-trigger"
                >
                  <span>
                    {value.length === 0
                      ? i18n._({ id: 'shifts.form.selectEmployee', message: 'Select an employee' })
                      : i18n._({ id: 'shifts.form.addEmployee', message: 'Add a person' })}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
                </button>
              </PopoverTrigger>
            </div>
          </PopoverAnchor>
          <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0" onOpenAutoFocus={(e) => {
              // The search takes the focus, so typing right after opening searches.
              e.preventDefault();
              searchRef.current?.focus();
            }}>
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={onSearchKeyDown}
                placeholder={i18n._({ id: 'shifts.form.searchEmployee', message: 'Search a person' })}
                aria-label={i18n._({ id: 'shifts.form.searchEmployee', message: 'Search a person' })}
                aria-controls={listId}
                aria-activedescendant={options[active] ? `${listId}-${options[active].value}` : undefined}
                className="h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                data-testid="employee-multiselect-search"
              />
            </div>
            <ul id={listId} role="listbox" aria-multiselectable="true" className="max-h-72 overflow-y-auto p-1">
              {options.length === 0 && (
                <li className="px-2 py-1.5 text-sm text-muted-foreground">
                  {i18n._({ id: 'shifts.form.noEmployeeFound', message: 'Nobody found' })}
                </li>
              )}
              {options.map((option, index) => {
                const selected = value.includes(option.value);
                const employee = byId.get(option.value);
                return (
                  <li
                    key={option.value}
                    id={`${listId}-${option.value}`}
                    role="option"
                    aria-selected={selected}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                      index === active && 'bg-accent text-accent-foreground'
                    )}
                    onMouseEnter={() => setActive(index)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => toggle(option.value)}
                    data-testid={option.value === OPEN_SHIFT_VALUE ? 'open-shift-option' : `employee-option-${option.value}`}
                  >
                    <Check className={cn('h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
                    {option.value === OPEN_SHIFT_VALUE ? (
                      <CircleDashed className="h-5 w-5 shrink-0" aria-hidden="true" />
                    ) : employee ? (
                      <EmployeeAvatar employee={{ id: employee.id, firstname: employee.firstname, lastname: employee.lastname, avatarUrl: employee.avatarUrl }} />
                    ) : null}
                    <span className={cn('truncate', option.value === OPEN_SHIFT_VALUE && 'font-medium')}>{option.label}</span>
                    {employee?.qualificationLevel === 'QUALIFIED' && (
                      <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-xs">
                        {i18n._({ id: 'employees.qualification.qualified', message: 'Qualified' })}
                      </Badge>
                    )}
                  </li>
                );
              })}
            </ul>
          </PopoverContent>
        </Popover>
      </TooltipProvider>
      {plannedGroupIds.length > 0 && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
          aria-pressed={showAll}
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll
            ? i18n._({ id: 'shifts.employeeSelect.showGroupOnly', message: 'Show only this group' })
            : i18n._({ id: 'shifts.employeeSelect.showAll', message: 'Show all employees' })}
        </Button>
      )}
    </div>
  );
}
